import { useState } from 'react';
import { useApplyCoupon, useRemoveCoupon } from '../../features/cart/useCart.js';
import { ApiError } from '../../lib/api.js';
import { Button } from '../ui/Button.jsx';
import { cn, formatMoney } from '../../lib/format.js';

/**
 * The coupon field, with live validation.
 *
 * The error comes from the server and is shown inline rather than as a toast,
 * because a shopper who mistyped a code needs to read the reason while looking
 * at the box they typed it into. The server's messages are specific by design —
 * "add ₹300 to use it" rather than "invalid coupon".
 */

export const CouponField = ({ cart, className }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');

  const apply = useApplyCoupon();
  const remove = useRemoveCoupon();

  const applied = cart?.coupon;

  const submit = (event) => {
    event.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) {
      setError('Enter a code.');
      return;
    }

    setError('');
    apply.mutate(trimmed, {
      onSuccess: () => setCode(''),
      onError: (failure) =>
        setError(failure instanceof ApiError ? failure.message : 'That code could not be applied.'),
    });
  };

  if (applied) {
    return (
      <div className={cn('flex items-start justify-between gap-3 border border-success/30 bg-success/8 p-3', className)}>
        <div className="min-w-0">
          <p className="text-2xs font-medium tracking-[0.1em] text-success uppercase">
            {applied.code} applied
          </p>
          <p className="mt-0.5 text-2xs text-muted">
            {applied.freeShipping
              ? 'Delivery is on us.'
              : `${formatMoney(applied.discount)} off${applied.description ? ` · ${applied.description}` : ''}`}
          </p>
        </div>

        <button
          type="button"
          onClick={() => remove.mutate()}
          disabled={remove.isPending}
          className="shrink-0 text-2xs text-muted underline decoration-mist underline-offset-4 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          Remove
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={className} noValidate>
      <label htmlFor="coupon-code" className="text-2xs font-medium tracking-[0.1em] text-ink uppercase">
        Discount code
      </label>

      <div className="mt-2 flex gap-2">
        <input
          id="coupon-code"
          value={code}
          onChange={(event) => {
            setCode(event.target.value.toUpperCase());
            setError('');
          }}
          placeholder="WELCOME10"
          autoComplete="off"
          autoCapitalize="characters"
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? 'coupon-error' : undefined}
          className={cn(
            'h-11 min-w-0 flex-1 rounded border bg-surface px-3 text-xs tracking-[0.08em] text-ink uppercase',
            'placeholder:normal-case placeholder:tracking-normal placeholder:text-muted/60',
            'focus:outline-2 focus:outline-offset-1 focus:outline-accent',
            error ? 'border-danger' : 'border-mist focus:border-brand',
          )}
        />
        <Button type="submit" variant="outline" loading={apply.isPending} loadingLabel="Checking">
          Apply
        </Button>
      </div>

      {error && (
        <p id="coupon-error" role="alert" className="mt-2 text-2xs text-danger">
          {error}
        </p>
      )}
    </form>
  );
};

export default CouponField;
